/**
 * Copyright (c) 2023, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
 *
 * WSO2 LLC. licenses this file to you under the Apache License,
 * Version 2.0 (the "License"); you may not use this file except
 * in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied. See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { ClickAwayListener, Fade, Link, Paper, Popper, Typography } from "@mui/material";
import PropTypes from "prop-types";
import React from "react";
import { useCloud } from "../Hooks/Cloud";

/**
 * Popper block
 * Description - This component is used to display the popper block.
 */
function PopperBlock({ id, open, setOpen, anchorEl }) {
  const { cloud } = useCloud();
  const features = window.ENV[cloud].features;
  const subscribe = window.ENV[cloud].subscribe;

  const updateOpen = (event) => {
    setOpen(event);
  };

  return (
    <Popper
      id={ id }
      open={ open }
      anchorEl={ anchorEl }
      transition
    >
      { ({ TransitionProps }) => (
        <ClickAwayListener onClickAway={ () => updateOpen(false) }>
          <Fade { ...TransitionProps } timeout={ 350 }>
            <Paper sx={ { p: 2 } } variant="outlined">
              { /* Display atom and rss feeds */ }
              { ((subscribe.atom || subscribe.rss) && features.subscribe) && (
                <Typography component={ "div" } variant="body2" sx={ { mb: 1 } }>
                  Get the { subscribe.atom && (
                    <Link
                      href={ subscribe.atom }
                      underline="none"
                      target="_blank"
                      rel="noopener noreferrer">Atom Feed</Link>) }
                  { subscribe.atom && subscribe.rss && (<span> or </span>) }
                  { subscribe.rss && (<Link
                    href={ subscribe.rss }
                    underline="none"
                    target="_blank"
                    rel="noopener noreferrer">RSS Feed</Link>) }.
                </Typography>
              ) }

              { /* Display support site */ }
              { subscribe.support && (
                <>
                  <Typography
                    component={ "div" }
                    variant="body2"
                    align="center"
                    sx={ {
                      borderBottomColor: "#e0e0e0",
                      borderBottomStyle: "solid",
                      borderBottomWidth: "1px", mb: 1
                    } } />
                  <Typography component={ "div" } variant="body2">
                    Visit our <Link
                      href={ subscribe.support }
                      underline="none"
                      target="_blank"
                      rel="noopener noreferrer">Support Site</Link>.
                  </Typography></>) }
            </Paper>
          </Fade>
        </ClickAwayListener>
      ) }
    </Popper>
  );
}

PopperBlock.propTypes = {
  anchorEl: PropTypes.instanceOf(Element),
  id: PropTypes.string,
  open: PropTypes.bool,
  setOpen: PropTypes.func
};

export default PopperBlock;
